import { Outlet, useLocation, Navigate } from 'react-router-dom';
import { useSubscriptionAccess } from '@/hooks/useSubscriptionAccess';
import { useAuth } from '@/contexts/AuthContext';
import { isPartnerUser } from '@/utils/partnerUtils';
import { SubscriptionRequired } from '@/components/ui/SubscriptionRequired';
import Sidebar from '@/components/navigation/Sidebar';
import { Toaster } from '@/components/ui/sonner';
import FloatingActionButton from '@/components/ui/FloatingActionButton';
import { AlertNotifications } from '@/components/ui/AlertNotifications';
import { PersistentAccountStatusBanner } from '@/components/ui/PersistentAccountStatusBanner';

// Define which routes should be protected by subscription
const PROTECTED_ROUTES = [
  '/dashboard/dashboard',
  '/dashboard/call-queue',
  '/dashboard/calls',
  '/dashboard/emails',
  '/dashboard/receptionist-settings',
  '/dashboard/hero-commands',
  '/dashboard/integrations',
  '/dashboard/resources'
];

// Routes that are always accessible
const ALWAYS_ACCESSIBLE_ROUTES = [
  '/dashboard/billing',
  '/dashboard/account'
];

export default function ProtectedDashboardLayout() {
  const location = useLocation();
  const { profile } = useAuth();
  const { hasAccess, isLoading } = useSubscriptionAccess();
  
  const isPartner = isPartnerUser(profile?.user_type);
  const currentPath = location.pathname;

  // Partner users have full access
  if (isPartner) {
    return <DashboardLayoutContent />;
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex h-screen bg-muted">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <main className="flex-1 overflow-y-auto bg-background p-4 md:p-6 lg:p-8">
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // Check if current route needs protection
  const isProtectedRoute = PROTECTED_ROUTES.includes(currentPath);
  const isAlwaysAccessible = ALWAYS_ACCESSIBLE_ROUTES.includes(currentPath);

  // If it's a protected route and user doesn't have access, show subscription required
  if (isProtectedRoute && !hasAccess) {
    return (
      <div className="flex h-screen bg-muted">
        <Sidebar />
        <div className="flex-1 flex flex-col overflow-hidden">
          <PersistentAccountStatusBanner />
          <main className="flex-1 overflow-y-auto bg-background">
            <SubscriptionRequired 
              feature={getFeatureName(currentPath)} 
              showOverlay={false}
            >
              <div className="p-4 md:p-6 lg:p-8">
                <div className="h-4"></div> {/* Spacer */}
              </div>
            </SubscriptionRequired>
          </main>
        </div>
        <Toaster richColors position="top-right" />
        <FloatingActionButton />
        <AlertNotifications />
      </div>
    );
  }

  // If it's always accessible or user has access, show normal layout
  if (isAlwaysAccessible || hasAccess) {
    return <DashboardLayoutContent />;
  }

  // Fallback - redirect to billing
  return <Navigate to="/dashboard/billing" replace />;
}

function DashboardLayoutContent() {
  return (
    <div className="flex h-screen bg-muted">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <PersistentAccountStatusBanner />
        <main className="flex-1 overflow-y-auto bg-background p-4 md:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
      <Toaster richColors position="top-right" />
      <FloatingActionButton />
      <AlertNotifications />
    </div>
  );
}

function getFeatureName(path: string): string {
  const featureMap: Record<string, string> = {
    '/dashboard/dashboard': 'dashboard',
    '/dashboard/call-queue': 'scheduled calls',
    '/dashboard/calls': 'call logs',
    '/dashboard/emails': 'email management',
    '/dashboard/receptionist-settings': 'AI employee settings',
    '/dashboard/hero-commands': 'Hero Commands™',
    '/dashboard/integrations': 'integrations',
    '/dashboard/resources': 'resources'
  };
  
  return featureMap[path] || 'this feature';
}
